import { pickBy } from 'lodash-es';
import { DateTime } from 'luxon';

import type { PgTeam } from '@poseidon/types/pg-gen/team';
import type { PgUserTeamData } from '@poseidon/types/pg-gen/userTeamData';
import type { SvrContactPrimaryFields } from '@poseidon/types/svr/svrContact.type';
import type { SvrOrg } from '@poseidon/types/svr/svrOrg.type';
import type { AtVariableDict } from '@poseidon/types/types/atVariables.type';
import type { ContactPrimaryFields } from '@poseidon/types/types/contact.type';
import type { OrgPrimaryFields } from '@poseidon/types/types/org.type';
import { AtVariableKeys } from '@poseidon/types/types/atVariables.type';

import { isNotEmpty } from '.';

export function extractAtVariablesV2(
  team: { name: PgTeam['name'] },
  userData: {
    name: PgUserTeamData['name'];
    title: PgUserTeamData['title'];
    calendar_link: PgUserTeamData['calendar_link'];
    contact_number: PgUserTeamData['contact_number'];
    email: PgUserTeamData['contact_email'];
  },
  contact_primary_fields: ContactPrimaryFields | null,
  org_primary_fields: OrgPrimaryFields | null,
  contact_about?: { liAbout?: string | null },
): AtVariableDict {
  const data: AtVariableDict = {
    Today: DateTime.now().toFormat('EEEE'),
  };

  const firstName = contact_primary_fields?.firstName?.data;
  if (isNotEmpty(firstName)) {
    data.FirstName = firstName;
  }

  const lastName = contact_primary_fields?.lastName?.data;
  if (isNotEmpty(lastName)) {
    data.LastName = lastName;
  }

  const fullName = firstName == null || lastName == null ? undefined : `${firstName} ${lastName}`;
  if (isNotEmpty(fullName)) {
    data.FullName = fullName;
  }

  const company = contact_primary_fields?.company?.data;
  if (isNotEmpty(company)) {
    data.Company = company;
  } else {
    // try looking at company full
    const companyFull = contact_primary_fields?.companyFull?.data;
    if (isNotEmpty(companyFull)) {
      data.Company = companyFull;
    }
  }

  const city = contact_primary_fields?.city?.data;
  if (isNotEmpty(city)) {
    data.City = city;
  }

  const title = contact_primary_fields?.title?.data;
  if (isNotEmpty(title)) {
    data.Title = title;
  }

  const headline = contact_primary_fields?.headline?.data;
  if (isNotEmpty(headline)) {
    data.Headline = headline;
  }

  const about = contact_about?.liAbout;
  if (isNotEmpty(about)) {
    data.About = about;
  }

  const teamName = team.name;
  if (isNotEmpty(teamName)) {
    data.MyTeam = teamName;
  }

  const myFirstName = userData.name?.split(' ')[0]?.trim();
  if (isNotEmpty(myFirstName)) {
    data.MyName = myFirstName;
    data.MyFirstName = myFirstName;
  }

  const myFullName = userData.name;
  if (isNotEmpty(myFullName)) {
    data.MyFullName = myFullName;
  }

  const myTitle = userData?.title;
  if (isNotEmpty(myTitle)) {
    data.MyTitle = myTitle;
  }

  const myCalendarLink = userData?.calendar_link;
  if (isNotEmpty(myCalendarLink)) {
    data.MyCalendarLink = myCalendarLink;
  }

  const myContactNumber = userData?.contact_number;
  if (isNotEmpty(myContactNumber)) {
    data.MyContactNumber = myContactNumber;
  }

  const myContactEmail = userData?.email;
  if (isNotEmpty(myContactEmail)) {
    data.MyContactEmail = myContactEmail;
  }

  if (org_primary_fields) {
    const companyNamePrimaryField = org_primary_fields?.name;
    const companyName = companyNamePrimaryField?.data;
    if (companyName != null) data.Company = companyName;
  }

  return data;
}

export function atVariablesPlaceholders() {
  const dict: AtVariableDict = {};

  AtVariableKeys.forEach((k) => {
    dict[k] = `{${k}}`;
  });
  return dict;
}

export function svrExtractAtVariables(
  userTeamData: {
    teamName: string;
    userTeamName: string | null;
    title: string | null;
    calendarLink: string | null;
    contactNumber: string | null;
    userTeamEmail: string | null;
  },
  contact: { primaryFields: SvrContactPrimaryFields; liAbout?: string | null } | null,
  org: SvrOrg | null | undefined,
) {
  const firstName = contact?.primaryFields?.firstName?.data;
  const lastName = contact?.primaryFields?.lastName?.data;
  const fullName = firstName != null && lastName != null ? `${firstName} ${lastName}` : undefined;
  const company = contact?.primaryFields?.company?.data;
  const companyFull = contact?.primaryFields?.companyFull?.data;
  const city = contact?.primaryFields?.city?.data;
  const title = contact?.primaryFields?.title?.data;
  const headline = contact?.primaryFields?.headline?.data;
  const about = contact?.liAbout;

  const myFirstName = userTeamData.userTeamName?.split(' ')[0]?.trim();
  const myFullName = userTeamData.userTeamName;
  const myTitle = userTeamData?.title;
  const myCalendarLink = userTeamData?.calendarLink;
  const myContactNumber = userTeamData?.contactNumber;
  const myContactEmail = userTeamData?.userTeamEmail;
  const orgCompanyName = org?.fields?.name?.find((f) => f.primary)?.data;

  const data: AtVariableDict = {
    Today: DateTime.now().toFormat('EEEE'),
    MyTeam: isNotEmpty(userTeamData.teamName) ? userTeamData.teamName : undefined,
    FirstName: isNotEmpty(firstName) ? firstName : undefined,
    LastName: isNotEmpty(lastName) ? lastName : undefined,
    FullName: isNotEmpty(fullName) ? fullName : undefined,
    Company: isNotEmpty(orgCompanyName)
      ? orgCompanyName
      : isNotEmpty(company)
        ? company
        : isNotEmpty(companyFull)
          ? companyFull
          : undefined,
    City: isNotEmpty(city) ? city : undefined,
    Title: isNotEmpty(title) ? title : undefined,
    Headline: isNotEmpty(headline) ? headline : undefined,
    About: isNotEmpty(about) ? about : undefined,
    
    MyName: isNotEmpty(myFirstName) ? myFirstName : undefined,
    MyFirstName: isNotEmpty(myFirstName) ? myFirstName : undefined,
    MyFullName: isNotEmpty(myFullName) ? myFullName : undefined,
    MyTitle: isNotEmpty(myTitle) ? myTitle : undefined,
    MyCalendarLink: isNotEmpty(myCalendarLink) ? myCalendarLink : undefined,
    MyContactNumber: isNotEmpty(myContactNumber) ? myContactNumber : undefined,
    MyContactEmail: isNotEmpty(myContactEmail) ? myContactEmail : undefined,
  };

  return pickBy(data, (v) => v !== undefined);
}
